/**
 * Named lists (#337, spec docs/superpowers/specs/2026-10-30-named-lists-design.md).
 *
 * A `namedList` block holds `namedListItem`s. An item is either direct (typed
 * into the list) or filed: `ref` names the `lineId` of a line elsewhere in the
 * same document, and the item holds a mirror of that line's text (kept in step
 * by listMirror.ts). Ticks, nesting, task links and carry-forward marks are
 * item attributes, so undo, autosave and stale-tab protection need nothing new.
 */
import { Node, InputRule, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey, Selection, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Fragment, Slice, type Node as PmNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { LINE_SOURCES, newLineId } from './LineIdExtension';
import { listMirrorPlugin, syncMirrors } from './listMirror';
import { NamedListView } from './NamedListView';
import { NamedListItemView } from './NamedListItemView';
import { listPrefixPlugin } from './listPrefix';

export type ListKind = 'checklist' | 'plain';
export interface ListInfo { listId: string; name: string; kind: ListKind; pos: number }

/** Opens the "which list?" picker to file the lines at these positions. */
export type OpenListPicker = (a: { positions: number[]; x: number; y: number; filter?: string }) => void;

/** List-level actions the host page provides; a menu entry shows only when its action is given. */
export interface NamedListOptions {
  onOpenListPicker?: OpenListPicker;
  /** Keep filed items in step with their lines. Off in editors that show only part of a document (an excerpt embed). */
  mirror?: boolean;
  onTurnOpenItemsIntoTasks?: (listId: string) => void;
  onBringForward?: (listId: string) => void;
}
export interface NamedListItemOptions {
  onTurnItemIntoTask?: (itemId: string) => void;
}

const newId = () => crypto.randomUUID();

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    namedLists: {
      insertNamedList: (attrs?: { name?: string; kind?: ListKind }) => ReturnType;
      renameNamedList: (listId: string, name: string) => ReturnType;
      setNamedListKind: (listId: string, kind: ListKind) => ReturnType;
      removeNamedList: (listId: string) => ReturnType;
      /** Direct item at the end of the list; `select: false` leaves the cursor where it is (right-panel quick-add). */
      addListItem: (listId: string, text?: string, opts?: { select?: boolean }) => ReturnType;
      toggleListItem: (itemId: string) => ReturnType;
      setItemCheckbox: (itemId: string, on: boolean) => ReturnType;
      /** File the lines at these positions into the list (each line once per list). */
      fileLinesIntoList: (listId: string, positions: number[]) => ReturnType;
    };
  }
}

// -- Lookups ---------------------------------------------------------------

export function listsIn(doc: PmNode): ListInfo[] {
  const out: ListInfo[] = [];
  doc.descendants((n, pos) => {
    if (n.type.name !== 'namedList') return true;
    out.push({ listId: n.attrs.listId, name: n.attrs.name, kind: n.attrs.kind, pos });
    return false;
  });
  return out;
}

function findNode(doc: PmNode, type: string, attr: string, id: string): { pos: number; node: PmNode } | null {
  let hit: { pos: number; node: PmNode } | null = null;
  doc.descendants((n, pos) => {
    if (hit) return false;
    if (n.type.name === type && n.attrs[attr] === id) { hit = { pos, node: n }; return false; }
    return true;
  });
  return hit;
}

export const findList = (doc: PmNode, listId: string) => findNode(doc, 'namedList', 'listId', listId);
export const findItem = (doc: PmNode, itemId: string) => findNode(doc, 'namedListItem', 'itemId', itemId);

/** done/total over an item's sub-checkboxes; null when it has none. */
export function itemProgress(item: PmNode): { done: number; total: number } | null {
  let done = 0;
  let total = 0;
  item.forEach((c) => {
    if (c.type.name !== 'namedListItem' || !c.attrs.checkbox) return;
    total++;
    if (c.attrs.checked) done++;
  });
  return total ? { done, total } : null;
}

/** The item whose own first paragraph holds the cursor. */
function currentItem(state: EditorState): { pos: number; node: PmNode; depth: number } | null {
  const { $from } = state.selection;
  const d = $from.depth - 1;
  if (d < 1) return null;
  const node = $from.node(d);
  if (node.type.name !== 'namedListItem' || $from.index(d) !== 0) return null;
  return { pos: $from.before(d), node, depth: d };
}

function listKindAt(state: EditorState, pos: number): ListKind {
  const $pos = state.doc.resolve(pos);
  for (let d = $pos.depth; d > 0; d--) {
    const n = $pos.node(d);
    if (n.type.name === 'namedList') return n.attrs.kind;
  }
  return 'checklist';
}

function makeItem(state: EditorState, attrs: Record<string, unknown>, content?: Fragment): PmNode {
  const { namedListItem, paragraph } = state.schema.nodes;
  return namedListItem.create({ itemId: newId(), ...attrs }, paragraph.create(null, content));
}

/** One position inside each line the selection touches (the cursor's line when empty). */
export function selectionLinePositions(state: EditorState): number[] {
  const { from, to, empty } = state.selection;
  if (empty) return [from];
  const positions: number[] = [];
  state.doc.nodesBetween(from, to, (n, pos) => {
    if (n.type.name === 'namedList') return false;
    if (LINE_SOURCES.has(n.type.name)) { positions.push(pos + 1); return false; }
    return true;
  });
  return positions;
}

// -- Keyboard ---------------------------------------------------------------

function enter(view: EditorView): boolean {
  const { state } = view;
  if (!state.selection.empty) return false;
  const cur = currentItem(state);
  if (!cur) return false;
  const { $from } = state.selection;
  const para = cur.node.firstChild!;
  const parent = $from.node(cur.depth - 1);
  if (para.content.size === 0 && cur.node.childCount === 1) {
    if (parent.type.name === 'namedListItem') return outdent(view);
    // An empty top-level item ends the list.
    const listPos = $from.before(cur.depth - 1);
    const tr = state.tr.delete(cur.pos, cur.pos + cur.node.nodeSize);
    const after = tr.mapping.map(listPos + parent.nodeSize);
    tr.insert(after, state.schema.nodes.paragraph.create());
    tr.setSelection(TextSelection.create(tr.doc, after + 1));
    view.dispatch(tr.scrollIntoView());
    return true;
  }
  // A filed item mirrors its line, so it is never split: Enter just starts a new item.
  const tail = cur.node.attrs.ref ? Fragment.empty : para.content.cut($from.parentOffset);
  const tr = state.tr;
  if (tail.size) tr.delete($from.pos, $from.end());
  const at = tr.mapping.map(cur.pos + cur.node.nodeSize);
  tr.insert(at, makeItem(state, { checkbox: cur.node.attrs.checkbox }, tail));
  tr.setSelection(TextSelection.create(tr.doc, at + 2));
  view.dispatch(tr.scrollIntoView());
  return true;
}

function indent(view: EditorView): boolean {
  const { state } = view;
  const cur = currentItem(state);
  if (!cur) return false;
  const { $from } = state.selection;
  const index = $from.index(cur.depth - 1);
  const parent = $from.node(cur.depth - 1);
  // The first item (of a list, or of an item's children - after its paragraph)
  // has nothing to nest under; still take Tab so focus stays in the list.
  if (index < (parent.type.name === 'namedListItem' ? 2 : 1)) return true;
  const prev = parent.child(index - 1);
  const prevPos = cur.pos - prev.nodeSize;
  const moved = cur.node.type.create(
    { ...cur.node.attrs, checkbox: listKindAt(state, cur.pos) === 'checklist' }, cur.node.content, cur.node.marks);
  const tr = state.tr.delete(cur.pos, cur.pos + cur.node.nodeSize);
  const at = prevPos + prev.nodeSize - 1;
  tr.insert(at, moved);
  tr.setSelection(TextSelection.create(tr.doc, at + ($from.pos - cur.pos)));
  view.dispatch(tr.scrollIntoView());
  return true;
}

function outdent(view: EditorView): boolean {
  const { state } = view;
  const cur = currentItem(state);
  if (!cur) return false;
  const { $from } = state.selection;
  const parent = $from.node(cur.depth - 1);
  if (parent.type.name !== 'namedListItem') return true;
  const parentPos = $from.before(cur.depth - 1);
  const tr = state.tr.delete(cur.pos, cur.pos + cur.node.nodeSize);
  const at = tr.mapping.map(parentPos + parent.nodeSize);
  tr.insert(at, cur.node);
  tr.setSelection(TextSelection.create(tr.doc, at + ($from.pos - cur.pos)));
  view.dispatch(tr.scrollIntoView());
  return true;
}

/** The item whose own (first) paragraph is the textblock `$pos` sits in. */
function itemOwning(state: EditorState, pos: number): { pos: number; node: PmNode } | null {
  const $p = state.doc.resolve(pos);
  const d = $p.depth - 1;
  if (d < 1 || $p.node(d).type.name !== 'namedListItem' || $p.index(d) !== 0) return null;
  return { pos: $p.before(d), node: $p.node(d) };
}

const isEmptyItem = (n: PmNode) => n.childCount === 1 && n.firstChild!.content.size === 0;

/**
 * Pull `lower` (an item with no children) up into the end of `upper`'s own
 * paragraph. Only typed items join: a filed item mirrors a line, so joining
 * would rewrite that line (review #1).
 */
function joinItems(view: EditorView, upper: { pos: number; node: PmNode }, lower: { pos: number; node: PmNode }): boolean {
  if (upper.node.attrs.ref || lower.node.attrs.ref || lower.node.childCount > 1) return true;
  const end = upper.pos + 1 + upper.node.firstChild!.nodeSize - 1;
  const tr = view.state.tr.insert(end, lower.node.firstChild!.content);
  const from = tr.mapping.map(lower.pos);
  tr.delete(from, from + lower.node.nodeSize);
  tr.setSelection(TextSelection.create(tr.doc, end));
  view.dispatch(tr.scrollIntoView());
  return true;
}

function backspace(view: EditorView): boolean {
  const { state } = view;
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset !== 0) return false;
  const cur = currentItem(state);
  if (!cur) return false;
  if (cur.node.attrs.checkbox && $from.node(cur.depth - 1).type.name === 'namedListItem') {
    view.dispatch(state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, checkbox: false }));
    return true;
  }
  const before = Selection.findFrom(state.doc.resolve(cur.pos), -1, true);
  if (isEmptyItem(cur.node)) {
    const tr = state.tr.delete(cur.pos, cur.pos + cur.node.nodeSize);
    if (before) tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(before.from)));
    view.dispatch(tr.scrollIntoView());
    return true;
  }
  const upper = before ? itemOwning(state, before.from) : null;
  // Nothing sensible to join with (first item, or the line above is not an item): stay put.
  return upper ? joinItems(view, upper, { pos: cur.pos, node: cur.node }) : true;
}

function del(view: EditorView): boolean {
  const { state } = view;
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset !== $from.parent.content.size) return false;
  const cur = currentItem(state);
  if (!cur) return false;
  const next = Selection.findFrom(state.doc.resolve($from.after()), 1, true);
  const lower = next ? itemOwning(state, next.from) : null;
  if (!lower) return true;
  if (isEmptyItem(lower.node)) {
    view.dispatch(state.tr.delete(lower.pos, lower.pos + lower.node.nodeSize));
    return true;
  }
  return joinItems(view, { pos: cur.pos, node: cur.node }, lower);
}

// -- Copies get fresh list and item ids -------------------------------------

function reidentifyLists(slice: Slice): Slice {
  const map = (fragment: Fragment): Fragment => {
    const nodes: PmNode[] = [];
    fragment.forEach((node) => {
      if (node.isText) { nodes.push(node); return; }
      const t = node.type.name;
      nodes.push(t === 'namedList' || t === 'namedListItem'
        ? node.type.create({ ...node.attrs, ...(t === 'namedList' ? { listId: newId() } : { itemId: newId() }) }, map(node.content), node.marks)
        : node.copy(map(node.content)));
    });
    return Fragment.fromArray(nodes);
  };
  return new Slice(map(slice.content), slice.openStart, slice.openEnd);
}

function fixListIds(state: EditorState): Transaction | null {
  const seen = new Set<string>();
  let tr: Transaction | null = null;
  state.doc.descendants((n, pos) => {
    const attr = n.type.name === 'namedList' ? 'listId' : n.type.name === 'namedListItem' ? 'itemId' : null;
    if (!attr) return true;
    const key = `${attr}:${n.attrs[attr]}`;
    if (!n.attrs[attr] || seen.has(key)) {
      tr ??= state.tr;
      tr.setNodeMarkup(pos, undefined, { ...n.attrs, [attr]: newId() });
    } else seen.add(key);
    return true;
  });
  return tr;
}

// -- Nodes ------------------------------------------------------------------

export const NamedListItem = Node.create<NamedListItemOptions>({
  name: 'namedListItem',
  content: 'paragraph namedListItem*',
  defining: true,
  // Ahead of Indent, TaskItem and the default keymap, so Tab/Enter/Backspace
  // and "[ ] " mean list things inside a list.
  priority: 1000,

  addAttributes() {
    const attr = (name: string, dflt: unknown, parse: (v: string | null) => unknown = (v) => v) => ({
      default: dflt,
      parseHTML: (el: HTMLElement) => parse(el.getAttribute(`data-${name}`)),
      renderHTML: (a: Record<string, unknown>) => (a[name] === dflt || a[name] == null ? {} : { [`data-${name}`]: String(a[name]) }),
    });
    const bool = (v: string | null) => v === 'true';
    return {
      itemId: { ...attr('item-id', null), renderHTML: (a: Record<string, unknown>) => (a.itemId ? { 'data-item-id': a.itemId } : {}), parseHTML: (el: HTMLElement) => el.getAttribute('data-item-id') },
      checked: { default: false, parseHTML: (el: HTMLElement) => bool(el.getAttribute('data-checked')), renderHTML: (a: Record<string, unknown>) => (a.checked ? { 'data-checked': 'true' } : {}) },
      checkbox: { default: false, parseHTML: (el: HTMLElement) => bool(el.getAttribute('data-checkbox')), renderHTML: (a: Record<string, unknown>) => (a.checkbox ? { 'data-checkbox': 'true' } : {}) },
      ref: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute('data-ref'), renderHTML: (a: Record<string, unknown>) => (a.ref ? { 'data-ref': a.ref } : {}) },
      taskId: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute('data-task-id'), renderHTML: (a: Record<string, unknown>) => (a.taskId ? { 'data-task-id': a.taskId } : {}) },
      carriedFrom: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute('data-carried-from'), renderHTML: (a: Record<string, unknown>) => (a.carriedFrom ? { 'data-carried-from': a.carriedFrom } : {}) },
    };
  },

  addOptions() { return {}; },

  parseHTML() { return [{ tag: 'div[data-list-item]' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-list-item': '' }), 0]; },

  addNodeView() { return ReactNodeViewRenderer(NamedListItemView); },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => enter(editor.view),
      Tab: ({ editor }) => indent(editor.view),
      'Shift-Tab': ({ editor }) => outdent(editor.view),
      Backspace: ({ editor }) => backspace(editor.view),
      Delete: ({ editor }) => del(editor.view),
    };
  },

  addInputRules() {
    return [new InputRule({
      find: /^\[ \]\s$/,
      handler: ({ state, range }) => {
        const cur = currentItem(state);
        if (!cur || cur.node.attrs.checkbox) return null;
        if (state.selection.$from.node(cur.depth - 1).type.name !== 'namedListItem') return null;
        state.tr.delete(range.from, range.to).setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, checkbox: true });
      },
    })];
  },
});

export const NamedList = Node.create<NamedListOptions>({
  name: 'namedList',
  group: 'block',
  content: 'namedListItem*',
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      listId: { default: null, parseHTML: (el) => el.getAttribute('data-list-id'), renderHTML: (a) => (a.listId ? { 'data-list-id': a.listId } : {}) },
      name: { default: 'List', parseHTML: (el) => el.getAttribute('data-name') ?? 'List', renderHTML: (a) => ({ 'data-name': a.name }) },
      kind: { default: 'checklist', parseHTML: (el) => (el.getAttribute('data-kind') === 'plain' ? 'plain' : 'checklist'), renderHTML: (a) => ({ 'data-kind': a.kind }) },
    };
  },

  addOptions() { return {}; },

  parseHTML() { return [{ tag: 'div[data-named-list]' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-named-list': '' }), 0]; },

  addNodeView() { return ReactNodeViewRenderer(NamedListView); },

  addKeyboardShortcuts() {
    return {
      'Alt-l': ({ editor }) => {
        const open = this.options.onOpenListPicker;
        if (!open) return false;
        const { from } = editor.state.selection;
        const positions = selectionLinePositions(editor.state);
        let x = 0;
        let y = 0;
        try { const c = editor.view.coordsAtPos(from); x = c.left; y = c.bottom; } catch { /* no layout (tests) */ }
        open({ positions, x, y });
        return true;
      },
    };
  },

  // "/list " on an otherwise empty line, outside a list, makes a list there
  // (Dossier has no slash menu, so this is a typed shortcut; plan ruling R4).
  addInputRules() {
    return [new InputRule({
      find: /^\/list\s$/,
      handler: ({ state }) => {
        const { $from } = state.selection;
        const para = $from.parent;
        if (para.type.name !== 'paragraph' || $from.depth !== 1) return null;
        if (para.textContent !== '/list') return null;
        const at = $from.before(1);
        const list = state.schema.nodes.namedList.create({ listId: newId(), name: 'List', kind: 'checklist' }, makeItem(state, {}));
        state.tr.replaceWith(at, at + para.nodeSize, list);
        state.tr.setSelection(TextSelection.create(state.tr.doc, at + 3));
      },
    })];
  },

  addCommands() {
    const setListAttrs = (listId: string, attrs: Record<string, unknown>) => ({ state, dispatch }: { state: EditorState; dispatch?: (tr: Transaction) => void }) => {
      const hit = findList(state.doc, listId);
      if (!hit) return false;
      dispatch?.(state.tr.setNodeMarkup(hit.pos, undefined, { ...hit.node.attrs, ...attrs }));
      return true;
    };
    const setItemAttrs = (itemId: string, attrs: (n: PmNode) => Record<string, unknown>) => ({ state, dispatch }: { state: EditorState; dispatch?: (tr: Transaction) => void }) => {
      const hit = findItem(state.doc, itemId);
      if (!hit) return false;
      dispatch?.(state.tr.setNodeMarkup(hit.pos, undefined, { ...hit.node.attrs, ...attrs(hit.node) }));
      return true;
    };
    return {
      insertNamedList: (attrs = {}) => ({ state, dispatch }) => {
        const list = state.schema.nodes.namedList.create(
          { listId: newId(), name: attrs.name?.trim() || 'List', kind: attrs.kind ?? 'checklist' },
          makeItem(state, {}));
        const tr = state.tr.replaceSelectionWith(list);
        // Put the cursor in the new list's first item.
        let at = -1;
        tr.doc.descendants((n, pos) => {
          if (at < 0 && n.type.name === 'namedList' && n.attrs.listId === list.attrs.listId) at = pos;
          return at < 0;
        });
        if (at >= 0) tr.setSelection(TextSelection.create(tr.doc, at + 3));
        dispatch?.(tr.scrollIntoView());
        return true;
      },
      renameNamedList: (listId, name) => setListAttrs(listId, { name: name.trim() || 'List' }),
      setNamedListKind: (listId, kind) => setListAttrs(listId, { kind }),
      removeNamedList: (listId) => ({ state, dispatch }) => {
        const hit = findList(state.doc, listId);
        if (!hit) return false;
        dispatch?.(state.tr.delete(hit.pos, hit.pos + hit.node.nodeSize));
        return true;
      },
      addListItem: (listId, text = '', opts = {}) => ({ state, dispatch }) => {
        const hit = findList(state.doc, listId);
        if (!hit) return false;
        const at = hit.pos + hit.node.nodeSize - 1;
        const tr = state.tr.insert(at, makeItem(state, {}, text ? Fragment.from(state.schema.text(text)) : undefined));
        if (opts.select !== false) tr.setSelection(TextSelection.create(tr.doc, at + 2 + text.length)).scrollIntoView();
        dispatch?.(tr);
        return true;
      },
      toggleListItem: (itemId) => setItemAttrs(itemId, (n) => ({ checked: !n.attrs.checked })),
      setItemCheckbox: (itemId, on) => setItemAttrs(itemId, () => ({ checkbox: on })),
      fileLinesIntoList: (listId, positions) => ({ state, dispatch }) => {
        if (!findList(state.doc, listId)) return false;
        const tr = state.tr;
        const sources: Array<{ pos: number; node: PmNode }> = [];
        const seenPos = new Set<number>();
        for (const p of positions) {
          const $p = state.doc.resolve(Math.max(0, Math.min(p, state.doc.content.size)));
          let src: { pos: number; node: PmNode } | null = null;
          let inList = false;
          for (let d = $p.depth; d > 0; d--) {
            const n = $p.node(d);
            if (n.type.name === 'namedListItem' || n.type.name === 'namedList') inList = true;
            if (!src && LINE_SOURCES.has(n.type.name)) src = { pos: $p.before(d), node: n };
          }
          if (!src && $p.nodeAfter && LINE_SOURCES.has($p.nodeAfter.type.name)) src = { pos: $p.pos, node: $p.nodeAfter };
          if (!src || inList || seenPos.has(src.pos)) continue;
          seenPos.add(src.pos);
          sources.push(src);
        }
        if (!sources.length) return false;
        const refs: string[] = [];
        for (const s of sources) {
          let id = s.node.attrs.lineId as string | null;
          if (!id) {
            id = newLineId();
            tr.setNodeMarkup(s.pos, undefined, { ...s.node.attrs, lineId: id });
          }
          refs.push(id);
        }
        const list = findList(tr.doc, listId)!;
        const have = new Set<string>();
        list.node.forEach((c) => { if (c.attrs.ref) have.add(c.attrs.ref); });
        const add: PmNode[] = [];
        sources.forEach((s, i) => {
          if (have.has(refs[i])) return;
          have.add(refs[i]);
          add.push(makeItem(state, { ref: refs[i] }, s.node.content));
        });
        if (add.length) tr.insert(list.pos + list.node.nodeSize - 1, add);
        dispatch?.(tr);
        return true;
      },
    };
  },

  // A filed item that drifted (an older copy, a server-side edit) is put back
  // in line with its source when the document opens; not an undoable edit.
  onCreate() {
    if (this.options.mirror === false) return;
    const tr = syncMirrors(this.editor.state, null);
    if (tr) this.editor.view.dispatch(tr.setMeta('addToHistory', false));
  },

  addProseMirrorPlugins() {
    return [...(this.options.mirror === false ? [] : [listMirrorPlugin()]), listPrefixPlugin(this.editor, () => this.options.onOpenListPicker), new Plugin({
      key: new PluginKey('namedLists'),
      props: {
        transformPasted: (slice, view) =>
          ((view as unknown as { dragging?: { move?: boolean } | null }).dragging?.move ? slice : reidentifyLists(slice)),
      },
      appendTransaction: (trs, _old, state) => (trs.some((t) => t.docChanged) ? fixListIds(state) : null),
    })];
  },
});
